import { ReactNode, useRef } from 'react';
import { Icon } from '../Icon.js';
import { Dropdown, DropdownProps } from './Dropdown.js';

// ── FilterRow root ────────────────────────────────────────────────────────────

interface FilterRowProps {
  children: ReactNode;
  borderBottom?: boolean;
}

function FilterRowRoot({ children, borderBottom = true }: FilterRowProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--evo-space-2)',
        padding: `0 var(--evo-content-pad-x) 0 var(--evo-list-pad-x)`,
        height: 'var(--evo-subnav-h)',
        flexShrink: 0,
        borderBottom: borderBottom ? '1px solid var(--evo-stroke)' : 'none',
      }}
    >
      {children}
    </div>
  );
}

// ── Search ────────────────────────────────────────────────────────────────────

interface SearchProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  onClear?: () => void;
  placeholder?: string;
  width?: number;
}

function Search({ value, onChange, onSubmit, onClear, placeholder = 'Search…', width }: SearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0', flexShrink: 0 }}>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onSubmit?.(); }}
        placeholder={placeholder}
        style={{
          height: 'var(--evo-search-h)',
          width: width !== undefined ? `${width}px` : 'var(--evo-search-w)',
          padding: '0 10px',
          background: 'var(--evo-bg-2)',
          border: '1px solid var(--evo-stroke)',
          borderRight: 'none',
          borderRadius: '4px 0 0 4px',
          color: 'var(--evo-fg-2)',
          fontFamily: 'var(--evo-font)',
          fontSize: '13px',
          outline: 'none',
        }}
      />
      <button
        type="button"
        onClick={() => onSubmit ? onSubmit() : inputRef.current?.focus()}
        style={{
          height: '32px', width: '32px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--evo-bg-2)',
          border: '1px solid var(--evo-stroke)',
          borderRadius: '0 4px 4px 0',
          color: 'var(--evo-cyan)', cursor: onSubmit ? 'pointer' : 'default',
        }}
      >
        <Icon name="search" size={14} />
      </button>
      {onClear && value && (
        <button
          type="button"
          onClick={onClear}
          style={{
            marginLeft: '4px', height: '24px', width: '24px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'transparent', border: 'none',
            color: 'var(--evo-fg-3)', cursor: 'pointer',
          }}
        >
          <Icon name="close" size={12} />
        </button>
      )}
    </div>
  );
}

// ── Dropdown wrapper ──────────────────────────────────────────────────────────

function FilterDropdown<T extends string | number>(props: DropdownProps<T>) {
  return <Dropdown<T> size="sm" {...props} />;
}

// ── Active filter chip ────────────────────────────────────────────────────────

interface ChipProps {
  label: string;
  onRemove: () => void;
}

function Chip({ label, onRemove }: ChipProps) {
  return (
    <div
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        height: '24px', padding: '0 8px',
        background: 'var(--evo-cyan-12)',
        border: '1px solid rgba(0,250,200,0.3)',
        borderRadius: '100px',
        color: 'var(--evo-cyan)',
        fontFamily: 'var(--evo-font)', fontSize: '11px', fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        style={{
          display: 'flex', alignItems: 'center', background: 'none',
          border: 'none', padding: 0, cursor: 'pointer',
          color: 'var(--evo-cyan)', lineHeight: 1,
        }}
      >
        <Icon name="close" size={10} />
      </button>
    </div>
  );
}

// ── Spacer ────────────────────────────────────────────────────────────────────

function Spacer() {
  return <div style={{ flex: 1 }} />;
}

// ── Action link ───────────────────────────────────────────────────────────────

interface ActionProps {
  label: string;
  onClick: () => void;
  tone?: 'muted' | 'cyan';
}

function Action({ label, onClick, tone = 'muted' }: ActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none', border: 'none', padding: '0 4px',
        cursor: 'pointer', flexShrink: 0,
        color: tone === 'cyan' ? 'var(--evo-cyan)' : 'var(--evo-fg-3)',
        fontFamily: 'var(--evo-font)', fontSize: '12px',
      }}
    >
      {label}
    </button>
  );
}

// ── Compose export ────────────────────────────────────────────────────────────

export const FilterRow = Object.assign(FilterRowRoot, {
  Search,
  Dropdown: FilterDropdown,
  Chip,
  Spacer,
  Action,
});
